import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils, EMPTY_PROJECT_DETAIL } from '../../utils/commonUtils';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import { getProductDetail, updateProduct, ProductDetailResponse } from '../../utils/product';
import { FormData, FormErrors, ProductFormUtils } from '../../utils/productCommon';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { getCategoryGroupDetail, updateProductInTree, findPathToNode, CategoryGroupTreeNode } from '../../utils/productCategoryGroup';
import { 
  Home,
  Package,
  Save,
  ArrowLeft,
  X,
  Check,
  AlertCircle,
  Info,
  DollarSign,
  Gift,
  Settings,
  FileText,
  RefreshCw,
  History,
  Trash2
} from 'lucide-react';

const ProductUpdate: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();
  const { productCode } = useParams<{ productCode: string }>();
  const location = useLocation();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('product-list');
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FormData>(ProductFormUtils.getDefaultFormData());
  const [errors, setErrors] = useState<FormErrors>({});
  const [originalData, setOriginalData] = useState<FormData | null>(null);

  // API state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [productMetadata, setProductMetadata] = useState<{
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  } | null>(null);

  // tree_codes 기반 카테고리/그룹 브레드크럼 (상품이 속한 그룹 목록)
  const [treeCodes, setTreeCodes] = useState<string[]>([]);
  /** treeCode별 루트→노드 경로(타이틀 배열). 한 줄에 하나씩 표시 */
  const [categoryBreadcrumbs, setCategoryBreadcrumbs] = useState<Array<{ treeCode: string; groupCode: string; pathTitles: string[] }>>([]);
  const [isLoadingCategoryGroups, setIsLoadingCategoryGroups] = useState(false);
  const [removingTreeCode, setRemovingTreeCode] = useState<string | null>(null);

  // Check if screen is mobile size
  ProductFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = ProductFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) return;
      try {
        setIsLoadingBrands(true);
        const projectCode = projectDetail.code || getProjectCode();
        if (!projectCode) return;
        const response = await getBrandListFlat(projectCode);
        if (response?.brands) setBrands(response.brands);
        else setBrands([]);
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        setBrands([]);
      } finally {
        setIsLoadingBrands(false);
      }
    };
    if (projectDetail) fetchBrands();
  }, [projectDetail]);

  // 상품 상세 정보 로드 (중복 호출 방지)
  const isFetchingRef = React.useRef(false);
  
  useEffect(() => {
    const loadProductDetail = async () => {
      if (!productCode) {
        setLoadError('상품 코드가 없습니다.');
        setIsLoading(false);
        return;
      }
      
      // 이미 요청 중이면 중복 호출 방지
      if (isFetchingRef.current) {
        return;
      }
      
      try {
        isFetchingRef.current = true;
        setIsLoading(true);
        setLoadError(null);
        setTreeCodes([]);
        setCategoryBreadcrumbs([]);

        const projectCode = getProjectCode();
        const product: ProductDetailResponse = await getProductDetail(projectCode, productCode);

        // 업데이트 API 전송 대상만 FormData로 매핑 (treeCodes, treeSortInfo 등은 응답 전용, 폼에 포함하지 않음)
        const productFormData: FormData = {
          brandCode: product.brandCode || '',
          categoryCode: product.categoryCode || '',
          itemCode: product.itemCode ?? '',
          status: product.status,
          name: product.name,
          shortName: product.shortName || '',
          posCode: product.posCode || '',
          originalPrice: product.originalPrice,
          salePrice: product.salePrice,
          comment: product.comment || '',
          rewardStampCount: product.rewardStampCount,
          rewardPointRatio: product.rewardPointRatio,
          discount: product.discount,
          voucher: product.voucher,
          reservation: product.reservation,
          delivery: product.delivery,
          shipping: product.shipping,
          description: product.description || '',
          createdAt: product.createdAt,
          updatedAt: product.updatedAt
        };
        setFormData(productFormData);
        setOriginalData(productFormData);

        // 응답 전용: treeCodes로 브레드크럼 구성 (API는 camelCase treeCodes 반환)
        const rawTreeCodes: string[] = product.treeCodes ?? [];
        setTreeCodes(rawTreeCodes);
        if (rawTreeCodes.length === 0) {
          setCategoryBreadcrumbs([]);
          setIsLoadingCategoryGroups(false);
        } else {
          setIsLoadingCategoryGroups(true);
          const list: Array<{ treeCode: string; groupCode: string; pathTitles: string[] }> = [];
          for (const treeCode of rawTreeCodes) {
            const colonIdx = treeCode.indexOf(':');
            const groupCode = colonIdx >= 0 ? treeCode.slice(0, colonIdx) : treeCode;
            const nodeCode = colonIdx >= 0 ? treeCode.slice(colonIdx + 1) : '';
            console.log('[ProductUpdate] 상품 그룹 상세 API 호출:', { projectCode, groupCode, treeCode, nodeCode });
            try {
              const detail = await getCategoryGroupDetail(projectCode, groupCode);
              const tree = (detail as { tree?: CategoryGroupTreeNode[] }).tree;
              const pathTitles =
                (tree?.length && nodeCode ? findPathToNode(tree, nodeCode) : null) ?? [detail.title || groupCode, nodeCode || treeCode];
              list.push({ treeCode, groupCode, pathTitles });
            } catch {
              list.push({ treeCode, groupCode, pathTitles: [groupCode, nodeCode || treeCode] });
            }
          }
          setCategoryBreadcrumbs(list);
          setIsLoadingCategoryGroups(false);
        }

        // 메타데이터 저장
        setProductMetadata({
          createdAt: product.createdAt,
          createdBy: product.createdBy,
          updatedAt: product.updatedAt,
          updatedBy: product.updatedBy
        });
      } catch (error: any) {
        console.error('상품 상세 정보 로드 오류:', error);
        const displayMessage = CommonUtils.getNetworkErrorMessage(error);
        setLoadError(displayMessage);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    loadProductDetail();
  }, [productCode]);

  // Track changes
  useEffect(() => {
    if (originalData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(originalData);
      setHasChanges(changed);
    }
  }, [formData, originalData]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    ProductFormUtils.handleInputChange(e, setFormData, setErrors);
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors = ProductFormUtils.validateUpdateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    await ProductFormUtils.handleUpdateSubmit(
      e,
      formData,
      validateForm,
      setIsSubmitting,
      setSubmitError,
      setHasChanges,
      setOriginalData,
      getProjectCode,
      productCode!,
      updateProduct,
      navigate,
      location
    );
  };

  // Reset form to original data
  const handleReset = () => {
    if (originalData) {
      setFormData({...originalData});
    }
    setErrors({});
    setHasChanges(false);
    setShowResetConfirm(false);
  };

  // 카테고리/그룹 트리에서 상품 제거 (상품마스터트리수정 API)
  const handleRemoveFromTree = async (treeCode: string) => {
    const projectCode = projectDetail?.code || getProjectCode();
    if (!projectCode || !productCode) return;
    try {
      setRemovingTreeCode(treeCode);
      await updateProductInTree(projectCode, productCode, { operation: 'remove', treeCode });
      setTreeCodes((prev) => prev.filter((t) => t !== treeCode));
      setCategoryBreadcrumbs((prev) => prev.filter((b) => b.treeCode !== treeCode));
    } catch (err) {
      console.error('트리에서 제거 실패:', err);
      alert(err instanceof Error ? err.message : '제거에 실패했습니다.');
    } finally {
      setRemovingTreeCode(null);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
          <p className="text-gray-600">상품 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <X className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-red-900 mb-2">오류 발생</h3>
            <p className="text-red-700 mb-4">{loadError}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                다시 시도
              </button>
              <button
                onClick={() => navigate('/products')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                목록으로
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar 
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail || EMPTY_PROJECT_DETAIL}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar 
          title="상품 수정" 
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail || undefined}
        />

        {/* Edit Product Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {/* Back Button and Status */}
            <div className="flex items-center justify-between mb-6">
              <button 
                onClick={() => navigate('/products')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                상품 목록으로
              </button>
              
              {hasChanges && (
                <span className="flex items-center text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  수정사항 있음
                </span>
              )}
            </div>

            {/* Product Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    {formData.name} ({formData.itemCode})
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                    {productMetadata && (
                      <>
                        <div className="flex items-center">
                          <History className="w-4 h-4 mr-2" />
                          생성: {formatDateTimeFromApi(productMetadata.createdAt) || productMetadata.createdAt} ({productMetadata.createdBy})
                        </div>
                        <div className="flex items-center">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          수정: {formatDateTimeFromApi(productMetadata.updatedAt) || productMetadata.updatedAt} ({productMetadata.updatedBy})
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                  <span className="text-red-700">{submitError}</span>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* 기본 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-6">
                  <Info className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">기본 정보</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Brand Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      브랜드
                    </label>
                    <select
                      name="brandCode"
                      value={formData.brandCode || ''}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.brandCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      disabled={isLoadingBrands}
                    >
                      <option value="">브랜드를 선택하세요</option>
                      {brands.map((brand) => {
                        const nbsp = '\u00A0';
                        const indent = (nbsp + nbsp).repeat(brand.depth);
                        const prefix = brand.depth > 0 ? 'ㄴ ' : '';
                        return (
                          <option key={brand.code} value={brand.code}>
                            {`${indent}${prefix}${brand.name} (${brand.code})`}
                          </option>
                        );
                      })}
                    </select>
                    {errors.brandCode && (
                      <p className="mt-1 text-sm text-red-600">{errors.brandCode}</p>
                    )}
                  </div>

                  {/* Category: tree_codes 브레드크럼 + 삭제 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      카테고리
                    </label>
                    {isLoadingCategoryGroups ? (
                      <div className="flex items-center min-h-[2.5rem]">
                        <p className="text-sm text-gray-500">카테고리 정보 로딩중</p>
                      </div>
                    ) : categoryBreadcrumbs.length === 0 ? (
                      <div className="flex items-center min-h-[2.5rem]">
                        <p className="text-sm text-gray-500">소속된 카테고리/그룹이 없습니다.</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {categoryBreadcrumbs.map((item) => (
                          <div
                            key={item.treeCode}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-gray-100 text-gray-800 text-sm"
                          >
                            <span className="flex-1 min-w-0">
                              {item.pathTitles.join(' > ')}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveFromTree(item.treeCode)}
                              disabled={removingTreeCode === item.treeCode}
                              className="p-0.5 rounded hover:bg-gray-200 text-gray-500 hover:text-red-600 disabled:opacity-50 flex-shrink-0"
                              title="이 카테고리/그룹에서 제거"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상품 코드
                    </label>
                    <input
                      type="text"
                      name="itemCode"
                      value={formData.itemCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      title="영문 대소문자, 숫자, 언더스코어(_), 하이픈(-)만 입력 가능"
                      className={`w-full px-3 py-2 border ${errors.itemCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="영문, 숫자, _, - 만 입력"
                    />
                    {errors.itemCode && <p className="mt-1 text-xs text-red-500">{errors.itemCode}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      상태 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.status ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="ACTIVE">활성</option>
                      <option value="INACTIVE">비활성</option>
                      <option value="PENDING">대기중</option>
                      <option value="ARCHIVED">보관됨</option>
                    </select>
                    {errors.status && (
                      <p className="mt-1 text-sm text-red-600">{errors.status}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      상품명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="상품명을 입력하세요"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      짧은 상품명
                    </label>
                    <input
                      type="text"
                      name="shortName"
                      value={formData.shortName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="짧은 상품명을 입력하세요"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      POS 코드
                    </label>
                    <input
                      type="text"
                      name="posCode"
                      value={formData.posCode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="POS 코드를 입력하세요"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      상품 소개
                    </label>
                    <input
                      type="text"
                      name="comment"
                      value={formData.comment}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="상품 소개를 입력하세요"
                    />
                  </div>
                </div>
              </div>

              {/* 가격 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-6">
                  <DollarSign className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">가격 정보</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      정가 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="originalPrice"
                      value={formData.originalPrice ?? ''}
                      onChange={handleInputChange}
                      min="0"
                      className={`w-full px-3 py-2 border ${errors.originalPrice ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="정가를 입력하세요"
                    />
                    {errors.originalPrice && (
                      <p className="mt-1 text-sm text-red-600">{errors.originalPrice}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      판매가 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="salePrice"
                      value={formData.salePrice ?? ''}
                      onChange={handleInputChange}
                      min="0"
                      className={`w-full px-3 py-2 border ${errors.salePrice ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="판매가를 입력하세요"
                    />
                    {errors.salePrice && (
                      <p className="mt-1 text-sm text-red-600">{errors.salePrice}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 적립 정보 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-6">
                  <Gift className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">적립 정보</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      적립 스탬프 수
                    </label>
                    <input
                      type="number"
                      name="rewardStampCount"
                      value={formData.rewardStampCount ?? ''}
                      onChange={handleInputChange}
                      min="0"
                      className={`w-full px-3 py-2 border ${errors.rewardStampCount ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="적립 스탬프 수를 입력하세요"
                    />
                    {errors.rewardStampCount && (
                      <p className="mt-1 text-sm text-red-600">{errors.rewardStampCount}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      포인트 적립률
                    </label>
                    <input
                      type="number"
                      name="rewardPointRatio"
                      value={formData.rewardPointRatio ?? ''}
                      onChange={handleInputChange}
                      min="0"
                      max="1"
                      step="0.01"
                      className={`w-full px-3 py-2 border ${errors.rewardPointRatio ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="포인트 적립률을 입력하세요 (0-1)"
                    />
                    {errors.rewardPointRatio && (
                      <p className="mt-1 text-sm text-red-600">{errors.rewardPointRatio}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 서비스 옵션 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-6">
                  <Settings className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">서비스 옵션</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="discount"
                      checked={formData.discount === 1}
                      onChange={(e) => ProductFormUtils.handleCheckboxChange('discount', e.target.checked, setFormData, setErrors)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="discount" className="ml-2 text-sm font-medium text-gray-700">
                      할인 가능
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="voucher"
                      checked={formData.voucher === 1}
                      onChange={(e) => ProductFormUtils.handleCheckboxChange('voucher', e.target.checked, setFormData, setErrors)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="voucher" className="ml-2 text-sm font-medium text-gray-700">
                      교환권 사용 가능
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="reservation"
                      checked={formData.reservation === 1}
                      onChange={(e) => ProductFormUtils.handleCheckboxChange('reservation', e.target.checked, setFormData, setErrors)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="reservation" className="ml-2 text-sm font-medium text-gray-700">
                      예약 가능
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="delivery"
                      checked={formData.delivery === 1}
                      onChange={(e) => ProductFormUtils.handleCheckboxChange('delivery', e.target.checked, setFormData, setErrors)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="delivery" className="ml-2 text-sm font-medium text-gray-700">
                      배달 가능
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="shipping"
                      checked={formData.shipping === 1}
                      onChange={(e) => ProductFormUtils.handleCheckboxChange('shipping', e.target.checked, setFormData, setErrors)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="shipping" className="ml-2 text-sm font-medium text-gray-700">
                      배송 가능
                    </label>
                  </div>
                </div>
              </div>

              {/* 상품 설명 */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-center mb-6">
                  <FileText className="w-5 h-5 mr-2" />
                  <h2 className="text-lg font-semibold text-gray-900">상품 설명</h2>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    상세 설명
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="상품에 대한 상세 설명을 입력하세요"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  className={`px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center ${!hasChanges && 'opacity-50 cursor-not-allowed'}`}
                  disabled={!hasChanges}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center ${(!hasChanges || isSubmitting) && 'opacity-50 cursor-not-allowed'}`}
                    disabled={!hasChanges || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        저장 중...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        변경사항 저장
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-yellow-500 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">변경사항 초기화</h3>
            </div>
            <p className="text-gray-600 mb-6">
              모든 변경사항이 초기화됩니다. 계속하시겠습니까?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductUpdate;